import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import { commercialPlans, type PlanCode } from '../../config/commercialPlans';
import { seatBands, type SeatBandCode } from '../../config/seatBands';
import { submitCommercialDemoRequest } from '../../lib/commercialApi';
import { trackMarketingEvent } from '../../lib/analytics';

const COMPANY_NAME_MAX = 200;
const CONTACT_NAME_MAX = 100;
const PHONE_MAX = 20;
const MESSAGE_MAX = 1000;
const HONEYPOT_MAX = 255;
const UNDERLINED_MESSAGE_ROWS = 4;
const DEFAULT_MESSAGE_ROWS = 3;
// Sent when the visitor has not decided yet, so the sales team does not read a
// pre-filled plan or team size as the visitor's own choice.
const UNSURE_OPTION = 'unsure';
type UnsureOption = typeof UNSURE_OPTION;

const seatBandValues = seatBands.map((band) => band.code) as [
  SeatBandCode,
  ...SeatBandCode[],
];
export type DemoRequestPlanCode = Exclude<PlanCode, 'free'>;
const planValues = commercialPlans
  .filter((plan) => plan.ctaType === 'demo')
  .map((plan) => plan.code) as [DemoRequestPlanCode, ...DemoRequestPlanCode[]];
const demoRequestPlans = commercialPlans.filter((plan) =>
  isDemoRequestPlanCode(plan.code)
);

type DemoRequestFormData = {
  companyName: string;
  contactName: string;
  workEmail: string;
  phone: string;
  employeeRange: SeatBandCode | UnsureOption;
  planInterest: DemoRequestPlanCode | UnsureOption;
  message: string;
  honeypot: string;
};

type FieldErrors = {
  companyName?: string;
  contactName?: string;
  workEmail?: string;
};

interface DemoRequestFormProps {
  variant?: 'full' | 'minimal';
  appearance?: 'boxed' | 'underlined';
  submitLabel?: string;
  helperText?: string;
  defaultPlanInterest?: DemoRequestPlanCode;
}

function isDemoRequestPlanCode(
  planCode: PlanCode
): planCode is DemoRequestPlanCode {
  return planValues.includes(planCode as DemoRequestPlanCode);
}

// `appearance-none` removes the native arrow; without a chevron a select reads
// as plain text. The glyph lives in index.css (`.select-chevron`).
function selectClasses(appearance: 'boxed' | 'underlined'): string {
  const chevronInset =
    appearance === 'underlined'
      ? '[--select-chevron-inset:0.15rem]'
      : '[--select-chevron-inset:0.9rem]';
  return `${inputClasses(undefined, appearance)} select-chevron pr-8 ${chevronInset}`;
}

function inputClasses(
  hasError?: boolean,
  appearance: 'boxed' | 'underlined' = 'boxed'
): string {
  if (appearance === 'underlined') {
    const base =
      'w-full min-w-0 appearance-none rounded-none border-b border-[var(--ir-muted)]/50 bg-transparent px-0 py-3 text-base text-[var(--ir-ink)] outline-none transition-all placeholder:text-[var(--ir-muted)] focus:border-[var(--ir-forest)] focus:shadow-[0_1px_0_0_var(--ir-forest)]';
    if (hasError) {
      return `${base} border-red-700`;
    }
    return base;
  }

  const base =
    'w-full border bg-white px-4 py-3 text-[13px] text-(--notion-text-primary) transition-colors placeholder:text-(--notion-text-placeholder) focus:outline-none focus:ring-4';
  if (hasError) {
    return `${base} border-[var(--notion-red)] focus:ring-[var(--notion-red-bg)]`;
  }
  return `${base} border-(--notion-border-light) focus:border-[var(--frontpage-accent)] focus:ring-[var(--frontpage-accent-soft)]`;
}

export function DemoRequestForm({
  variant = 'full',
  appearance = 'boxed',
  submitLabel,
  helperText,
  defaultPlanInterest,
}: DemoRequestFormProps): React.JSX.Element {
  const { t, i18n } = useTranslation('common');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [hasTrackedStart, setHasTrackedStart] = useState(false);
  const fieldIdPrefix = useId();
  const errorIdFor = (field: keyof FieldErrors): string =>
    `${fieldIdPrefix}-${field}-error`;

  const [formData, setFormData] = useState<DemoRequestFormData>({
    companyName: '',
    contactName: '',
    workEmail: '',
    phone: '',
    employeeRange: UNSURE_OPTION,
    planInterest: defaultPlanInterest ?? UNSURE_OPTION,
    message: '',
    honeypot: '',
  });

  // The page can point the form at a plan after mount (a visitor picks a plan
  // card and is scrolled here); what they already typed stays untouched.
  useEffect(() => {
    if (!defaultPlanInterest) {
      return;
    }
    setFormData((previous) => ({
      ...previous,
      planInterest: defaultPlanInterest,
    }));
  }, [defaultPlanInterest]);

  const demoRequestSchema = useMemo(
    () =>
      z.object({
        companyName: z
          .string()
          .min(1, t('forms.demo.validation.companyRequired'))
          .max(COMPANY_NAME_MAX),
        contactName: z
          .string()
          .min(1, t('forms.demo.validation.contactRequired'))
          .max(CONTACT_NAME_MAX),
        workEmail: z.string().email(t('forms.demo.validation.emailInvalid')),
        phone: z.string().max(PHONE_MAX).optional(),
        employeeRange: z.enum([...seatBandValues, UNSURE_OPTION]),
        planInterest: z.enum([...planValues, UNSURE_OPTION]),
        message: z.string().max(MESSAGE_MAX).optional(),
        honeypot: z.string().max(HONEYPOT_MAX).optional(),
      }),
    [t]
  );

  const validateField = useCallback(
    (field: keyof FieldErrors, value: string) => {
      const result = demoRequestSchema.shape[field].safeParse(value);
      setFieldErrors((prev) => ({
        ...prev,
        [field]: result.success ? undefined : result.error.issues[0]?.message,
      }));
    },
    [demoRequestSchema]
  );

  const handleBlur = useCallback(
    (field: keyof FieldErrors, value: string) => {
      validateField(field, value);
    },
    [validateField]
  );

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setError('');
    setFieldErrors({});

    const validation = demoRequestSchema.safeParse(formData);
    if (!validation.success) {
      const newErrors: FieldErrors = {};
      for (const issue of validation.error.issues) {
        const field = issue.path[0] as keyof FieldErrors;
        if (field && !newErrors[field]) {
          newErrors[field] = issue.message;
        }
      }
      setFieldErrors(newErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const sourcePage =
        typeof window !== 'undefined' ? window.location.pathname : '/';
      const locale = i18n.resolvedLanguage || i18n.language || 'es';

      await submitCommercialDemoRequest(
        {
          companyName: formData.companyName,
          contactName: formData.contactName,
          workEmail: formData.workEmail,
          phone: formData.phone,
          employeeRange: formData.employeeRange,
          planInterest: formData.planInterest,
          message: formData.message,
          locale,
          source: 'inmueble_red_frontpage',
          sourcePage,
          honeypot: formData.honeypot,
        },
        import.meta.env.VITE_BACKEND_API_URL
      );

      trackMarketingEvent('demo_form_submit_success', {
        variant,
        sourcePage,
        planInterest:
          variant === 'full' && formData.planInterest !== UNSURE_OPTION
            ? formData.planInterest
            : undefined,
        employeeRange:
          variant === 'full' && formData.employeeRange !== UNSURE_OPTION
            ? formData.employeeRange
            : undefined,
      });

      setIsSuccess(true);
      setFormData({
        companyName: '',
        contactName: '',
        workEmail: '',
        phone: '',
        employeeRange: UNSURE_OPTION,
        planInterest: defaultPlanInterest ?? UNSURE_OPTION,
        message: '',
        honeypot: '',
      });
    } catch (parseError) {
      if (parseError instanceof Error) {
        const loweredMessage = parseError.message.toLowerCase();
        const errorType = loweredMessage.includes('timeout')
          ? 'network'
          : loweredMessage.includes('request')
            ? 'network'
            : 'unknown';
        trackMarketingEvent('demo_form_submit_error', {
          variant,
          sourcePage:
            typeof window !== 'undefined' ? window.location.pathname : '/',
          errorType,
        });
        setError(parseError.message);
      } else {
        trackMarketingEvent('demo_form_submit_error', {
          variant,
          sourcePage:
            typeof window !== 'undefined' ? window.location.pathname : '/',
          errorType: 'unknown',
        });
        setError(t('common.error'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const labelClassName =
    appearance === 'underlined'
      ? 'text-xs font-bold text-[var(--ir-muted)] uppercase tracking-[0.12em]'
      : 'mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-(--notion-text-secondary)';
  const buttonClassName =
    appearance === 'underlined'
      ? 'w-full rounded-full border border-[var(--ir-mint)] bg-[var(--ir-mint)] px-8 py-4 text-lg font-bold text-[var(--ir-deep)] transition-all duration-200 hover:bg-[#55dc78] disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--ir-terra)]'
      : 'w-full border border-(--frontpage-accent) bg-(--frontpage-accent) px-4 py-3 font-mono-brand text-xs font-medium uppercase tracking-[0.14em] text-white transition-colors hover:bg-[var(--ir-terra-dark)] disabled:cursor-not-allowed disabled:opacity-50';
  const helperClassName =
    appearance === 'underlined'
      ? 'text-center text-xs leading-relaxed text-[var(--ir-muted)]'
      : 'font-mono-brand text-xs leading-6 uppercase tracking-[0.14em] text-(--notion-text-secondary)';
  const errorClassName =
    appearance === 'underlined'
      ? 'text-sm text-red-700'
      : 'mt-1 text-xs text-(--notion-red)';

  if (isSuccess) {
    return (
      <div
        role="status"
        className={
          appearance === 'underlined'
            ? 'border border-brand-primary/10 bg-white p-8 text-center'
            : 'border border-(--notion-green-bg) bg-(--notion-green-bg) p-6 text-center'
        }
      >
        <svg
          aria-hidden="true"
          className="mx-auto mb-3 h-12 w-12 text-[var(--ir-forest)]"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <p className="text-lg font-medium text-[var(--ir-forest)]">
          {t('common.success')}
        </p>
        <p className="mt-2 text-sm text-[var(--ir-muted)]">
          {t('forms.demo.successSubtitle')}
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      onFocusCapture={() => {
        if (hasTrackedStart) {
          return;
        }
        const sourcePage =
          typeof window !== 'undefined' ? window.location.pathname : '/';
        trackMarketingEvent('demo_form_start', {
          variant,
          sourcePage,
        });
        setHasTrackedStart(true);
      }}
      className={appearance === 'underlined' ? 'space-y-8' : 'space-y-5'}
      noValidate
    >
      <input
        type="text"
        name="honeypot"
        value={formData.honeypot}
        onChange={(event) =>
          setFormData({ ...formData, honeypot: event.target.value })
        }
        className="hidden"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />

      <div
        className={
          appearance === 'underlined'
            ? 'grid grid-cols-1 gap-8 md:grid-cols-2'
            : 'grid grid-cols-1 gap-4 md:grid-cols-2'
        }
      >
        <div className={appearance === 'underlined' ? 'space-y-3' : ''}>
          <label htmlFor="demo-company-name" className={labelClassName}>
            {t('forms.demo.companyNameLabel')}
          </label>
          <input
            id="demo-company-name"
            name="companyName"
            autoComplete="organization"
            type="text"
            required
            value={formData.companyName}
            onChange={(event) =>
              setFormData({ ...formData, companyName: event.target.value })
            }
            onBlur={() => handleBlur('companyName', formData.companyName)}
            aria-invalid={fieldErrors.companyName ? true : undefined}
            aria-describedby={
              fieldErrors.companyName ? errorIdFor('companyName') : undefined
            }
            className={inputClasses(!!fieldErrors.companyName, appearance)}
            placeholder={t('forms.demo.companyNamePlaceholder')}
          />
          {fieldErrors.companyName ? (
            <p id={errorIdFor('companyName')} className={errorClassName}>
              {fieldErrors.companyName}
            </p>
          ) : null}
        </div>

        <div className={appearance === 'underlined' ? 'space-y-3' : ''}>
          <label htmlFor="demo-contact-name" className={labelClassName}>
            {t('forms.demo.contactNameLabel')}
          </label>
          <input
            id="demo-contact-name"
            name="contactName"
            autoComplete="name"
            type="text"
            required
            value={formData.contactName}
            onChange={(event) =>
              setFormData({ ...formData, contactName: event.target.value })
            }
            onBlur={() => handleBlur('contactName', formData.contactName)}
            aria-invalid={fieldErrors.contactName ? true : undefined}
            aria-describedby={
              fieldErrors.contactName ? errorIdFor('contactName') : undefined
            }
            className={inputClasses(!!fieldErrors.contactName, appearance)}
            placeholder={t('forms.demo.contactNamePlaceholder')}
          />
          {fieldErrors.contactName ? (
            <p id={errorIdFor('contactName')} className={errorClassName}>
              {fieldErrors.contactName}
            </p>
          ) : null}
        </div>
      </div>

      <div
        className={
          appearance === 'underlined'
            ? 'space-y-3'
            : 'grid grid-cols-1 gap-4 md:grid-cols-2'
        }
      >
        <div className={appearance === 'underlined' ? 'space-y-3' : ''}>
          <label htmlFor="demo-work-email" className={labelClassName}>
            {t('forms.demo.emailLabel')}
          </label>
          <input
            id="demo-work-email"
            name="workEmail"
            autoComplete="email"
            type="email"
            required
            value={formData.workEmail}
            onChange={(event) =>
              setFormData({ ...formData, workEmail: event.target.value })
            }
            onBlur={() => handleBlur('workEmail', formData.workEmail)}
            aria-invalid={fieldErrors.workEmail ? true : undefined}
            aria-describedby={
              fieldErrors.workEmail ? errorIdFor('workEmail') : undefined
            }
            className={inputClasses(!!fieldErrors.workEmail, appearance)}
            placeholder={t('forms.demo.emailPlaceholder')}
          />
          {fieldErrors.workEmail ? (
            <p id={errorIdFor('workEmail')} className={errorClassName}>
              {fieldErrors.workEmail}
            </p>
          ) : null}
        </div>

        <div className={appearance === 'underlined' ? 'space-y-3' : ''}>
          <label htmlFor="demo-phone" className={labelClassName}>
            {t('forms.demo.phoneLabel')}
          </label>
          <input
            id="demo-phone"
            name="phone"
            autoComplete="tel"
            inputMode="tel"
            type="tel"
            value={formData.phone}
            onChange={(event) =>
              setFormData({ ...formData, phone: event.target.value })
            }
            className={inputClasses(undefined, appearance)}
            placeholder={t('forms.demo.phonePlaceholder')}
          />
        </div>
      </div>

      {variant === 'full' ? (
        <>
          <div
            className={
              appearance === 'underlined'
                ? 'grid grid-cols-1 gap-8 md:grid-cols-2'
                : 'grid grid-cols-1 gap-4 md:grid-cols-2'
            }
          >
            <div className={appearance === 'underlined' ? 'space-y-3' : ''}>
              <label htmlFor="demo-employee-range" className={labelClassName}>
                {t('forms.demo.employeeRangeLabel')}
              </label>
              <select
                id="demo-employee-range"
                name="employeeRange"
                value={formData.employeeRange}
                onChange={(event) =>
                  setFormData({
                    ...formData,
                    employeeRange: event.target.value as
                      SeatBandCode | UnsureOption,
                  })
                }
                className={selectClasses(appearance)}
              >
                <option value={UNSURE_OPTION}>
                  {t('forms.demo.unsureOption')}
                </option>
                {seatBands.map((band) => (
                  <option key={band.code} value={band.code}>
                    {t(band.labelKey)}
                  </option>
                ))}
              </select>
            </div>

            <div className={appearance === 'underlined' ? 'space-y-3' : ''}>
              <label htmlFor="demo-plan-interest" className={labelClassName}>
                {t('forms.demo.planInterestLabel')}
              </label>
              <select
                id="demo-plan-interest"
                name="planInterest"
                value={formData.planInterest}
                onChange={(event) =>
                  setFormData({
                    ...formData,
                    planInterest: event.target.value as
                      DemoRequestPlanCode | UnsureOption,
                  })
                }
                className={selectClasses(appearance)}
              >
                <option value={UNSURE_OPTION}>
                  {t('forms.demo.unsureOption')}
                </option>
                {demoRequestPlans.map((plan) => (
                  <option key={plan.code} value={plan.code}>
                    {t(plan.nameKey)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className={appearance === 'underlined' ? 'space-y-3' : ''}>
            <label htmlFor="demo-message" className={labelClassName}>
              {t('forms.demo.messageLabel')}
            </label>
            <textarea
              id="demo-message"
              name="message"
              value={formData.message}
              onChange={(event) =>
                setFormData({ ...formData, message: event.target.value })
              }
              rows={
                appearance === 'underlined'
                  ? UNDERLINED_MESSAGE_ROWS
                  : DEFAULT_MESSAGE_ROWS
              }
              className={inputClasses(undefined, appearance)}
              placeholder={t('forms.demo.messagePlaceholder')}
            />
          </div>
        </>
      ) : null}

      {error ? (
        <div
          role="alert"
          className={
            appearance === 'underlined'
              ? 'text-sm text-red-700'
              : 'border border-(--notion-red-bg) bg-(--notion-red-bg) p-3 text-sm text-(--notion-red)'
          }
        >
          {error}
        </div>
      ) : null}

      <p className={helperClassName}>
        {t('forms.demo.privacyNoticePrefix')}{' '}
        <Link to="/privacy" className="underline underline-offset-4">
          {t('forms.demo.privacyNoticeLink')}
        </Link>
        {t('forms.demo.privacyNoticeSuffix')}
      </p>

      <button type="submit" disabled={isSubmitting} className={buttonClassName}>
        {isSubmitting
          ? t('common.sending')
          : (submitLabel ?? t('common.submit'))}
      </button>

      <p className={helperClassName}>
        {helperText ?? t('forms.demo.successSubtitle')}
      </p>
    </form>
  );
}
